import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Inject,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { filter, map, switchMap, tap, first } from 'rxjs/operators';
import { untilDestroyed } from 'ngx-take-until-destroy';
import { UserMenuItemEnum } from './enums/user-menu-item.enum';
import { AppUrls } from '../../app-urls';
import { BehaviorSubject, Observable } from 'rxjs';
import { OpenSubMenuPayload } from './interfaces/open-sub-menu-payload.interface';
import { MenuFacade } from './interfaces/menu-facade.interface';
import { MenuItem } from './interfaces/menu-item.interface';
import { MAIN_MENU_FACADE_TOKEN } from './tokens/main-menu-facade.token';
import { LinkTypeEnum } from '../../enums/link-type.enum';

@Component({
  selector: 'app-main-menu',
  templateUrl: './main-menu.component.html',
  styleUrls: ['./main-menu.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MainMenuComponent implements OnInit, OnDestroy {
  public menuItems$: Observable<MenuItem[]>;
  public subMenu$: Observable<MenuItem[]>;
  public isRegulationsVisible$: Observable<boolean>;
  public submenuIsVisible: boolean = false;
  public activeSectionId: string;
  private _selectedMenuIndexSubject$ = new BehaviorSubject<number>(null);
  private _selectedSubMenuItemSubject$ = new BehaviorSubject<MenuItem>(null);

  constructor(
    @Inject(MAIN_MENU_FACADE_TOKEN)
    private _facade: MenuFacade,
    private _router: Router,
    private _cdr: ChangeDetectorRef
  ) {
    this._router.events
      .pipe(
        untilDestroyed(this),
        filter((event) => event instanceof NavigationEnd),
        tap(() => this._initActiveSectionId(this._router.url))
      )
      .subscribe();
  }

  public ngOnInit(): void {
    this._facade.buildMenu().pipe(first(), untilDestroyed(this)).subscribe();
    this.menuItems$ = this._facade.menuItems$;
    this.isRegulationsVisible$ = this._facade.isRegulationsVisible$;
    this.subMenu$ = this._selectedMenuIndexSubject$.pipe(
      switchMap((index) => {
        return this.menuItems$.pipe(map((menuItems) => menuItems[index].items));
      })
    );
  }

  public hideRegulationsWindow(): void {
    this._facade.hideRegulationsWindow();
  }

  public onOpenSubMenu({ index, hasSubmenu }: OpenSubMenuPayload) {
    this.submenuIsVisible =
      hasSubmenu && (!this.submenuIsVisible || this._selectedMenuIndexSubject$.value !== index);

    this._selectedMenuIndexSubject$.next(index);
  }

  public onSubmenuClick(link: MenuItem): void {
    this._selectedSubMenuItemSubject$.next(link)
    if (link.urlType !== LinkTypeEnum.URL_NEW_TAB) {
      this.submenuIsVisible = false;
    }
  }

  public setRegulationsAccept(): void {
    this._facade
      .setRegulationsAccept()
      .pipe(untilDestroyed(this))
      .subscribe();
  }

  private _initActiveSectionId(currentUrl: string) {
    const routesMapping = [
      { url: AppUrls.angularRoutes.customer.offerRegistry, typeId: UserMenuItemEnum.REGISTRIES_CATEGORY },
      { url: AppUrls.angularRoutes.customer.offerDetails, typeId: UserMenuItemEnum.REGISTRIES_CATEGORY },
      { url: AppUrls.angularRoutes.customer.compareOffer, typeId: UserMenuItemEnum.SERVICES_CATEGORY },
    ];

    this.activeSectionId =
      routesMapping.find((route) => (currentUrl.includes(route.url) || route.url.includes(currentUrl)))?.typeId || null;
    this._cdr.markForCheck();
  }

  public ngOnDestroy(): void {}
}
